import { Injectable } from '@angular/core';
import { FavoriteDataContextInterface } from '@modules/favorites/interfaces/favorite-data-context.interface';
import { MediaImageInterface } from '@modules/media/models/media-image.model';
import { MediaVideoInterface } from '@modules/media/models/media-video.model';
import { SectionType } from '@modules/section/enums/section-type.enum';
import {
  AnnouncementBannerInterface,
  AnnouncementBannerLayoutType,
} from '@modules/section/models/announcement-banner.model';
import { AnnouncementInterface } from '@modules/section/models/announcement.model';
import { CarouselLayoutTypes } from '@modules/section/models/carousel-section.model';
import { TranslateService } from '@ngx-translate/core';
import { Magazine } from '@pages/magazine-page/models/magazine.model';
import { z } from 'zod';

import { AnnouncementMultiImageStyle } from '../domain/image-styles/announcement-multi.style';
import { AnnouncementSmallImageStyle } from '../domain/image-styles/announcement-small.style';
import { CarouselSection_9_16_ImageStyle } from '../domain/image-styles/carousel-9-16.style';
import { CarouselSectionLandscapeImageStyle } from '../domain/image-styles/carousel-landscape.style';
import { CarouselSectionPortraitImageStyle } from '../domain/image-styles/carousel-portrait.style';
import { CarouselSectionSquareImageStyle } from '../domain/image-styles/carousel-square.style';
import { Equal3SectionImageStyle } from '../domain/image-styles/equal3-image.style';
import { Equal4SectionImageStyle } from '../domain/image-styles/equal4-image.style';
import { Hero1SectionImageStyle } from '../domain/image-styles/hero1-image.style';
import { Hero2SectionImageStyle } from '../domain/image-styles/hero2-image.style';
import { Hero3SectionImageStyleLarge, Hero3SectionImageStyleSmall } from '../domain/image-styles/hero3-image.style';
import { SquareSectionImageStyleLarge, SquareSectionImageStyleSmall } from '../domain/image-styles/square-image.style';
import {
  StaggeredSectionImageStyleLarge,
  StaggeredSectionImageStyleSmall,
} from '../domain/image-styles/staggered-image.style';
import { StageMediaCardImageStyle, StreamCardImageStyle } from '../domain/image-styles/stream-card.style';
import { TestCarImageStyle } from '../domain/image-styles/test-car-image.style';

import { Adapter } from './adapter';
import { parseAgendaSection } from './agenda.adapter';
import { ContactPersonAdapter, ContactPersonResponse } from './contact.adapter';
import { HighlightAdapter } from './highlight.adapter';
import { InfoSectionAdapter } from './info-section.adapter';
import { MagazineCardAdapter, MagazineCardResponse } from './magazine-card.adapter';
import { MediaAdapter } from './media.adapter';
import { PressKitAdapter } from './press-kit.adapter';
import { StreamAdapter } from './stream.adapter';
import { TestCarSchema } from './test-car-page-adapter';
import { TestRouteAdapter, TestRouteResponse } from './test-route.adapter';

export const MagazineSchema = z
  .object({
    id: z.string().uuid(),
    title: z.string(),
    description: z.string().nullable().optional(),
    slug: z
      .object({
        lang: z.string(),
        value: z.string(),
      })
      .array(),
    sections: z.any().array(), // TODO: create section schemata
  })
  .transform((input) => ({
    id: input.id,
    title: input.title,
    description: input.description,
    currentSlug: '',
    slugs: input.slug.map(({ value, lang }) => ({
      lang,
      value: value.replace('/page/', ''),
    })),
    sections: input.sections,
  }));

export type MagazineData = z.infer<typeof MagazineSchema>;
@Injectable({
  providedIn: 'root',
})
export class MagazineAdapter implements Adapter<Magazine> {
  constructor(
    private magazineCardAdapter: MagazineCardAdapter,
    private mediaAdapter: MediaAdapter,
    private streamAdapter: StreamAdapter,
    private testRouteAdapter: TestRouteAdapter,
    private contactAdapter: ContactPersonAdapter,
    private translateService: TranslateService,
    private highlightAdapter: HighlightAdapter,
    private pressKitAdapter: PressKitAdapter
  ) {}

  /**
   * Safely parses incoming data
   *
   * @param data unknown input data
   * @throws ZodError
   * @returns magazine data
   */
  public parse(data: any): Magazine {
    // TODO data: unknown
    // FIXME: workaround while zod SectionSchemas are still WIP
    const sections: any[] = [];
    data.sections.forEach((_section: any) => {
      try {
        const s = this.parseSection(_section, {
          id: data.id,
          type: 'pages',
        });
        sections.push(s);
      } catch (e) {
        console.error('Error parsing magazine/page section', e);
      }
    });
    data = {
      ...data,
      sections,
    };
    const result = MagazineSchema.safeParse(data);
    if (!result.success) {
      console.error('Error parsing magazine/page data', result.error);
      throw result.error; // TODO: create custom error type here?
    } else {
      result.data.currentSlug =
        result.data.slugs.find(({ lang }) => lang === this.translateService.currentLang.split('-')[0])?.value || 'home';
      return new Magazine(result.data);
    }
  }

  // TODO: move section parse logic into section models (use zod schema if possible)
  //       all logic that cannot be done with zod (data parsing), should go into the model constructor
  //       TODO: refactor such that this context is not needed
  private parseSection(data: any, context: FavoriteDataContextInterface) {
    let parsed: any = data;
    // some sections are now of type magazine_sections with their previous section type as attribute
    data.type = data.type === 'magazine_section' && data.section_type ? `${data.section_type}_section` : data.type;
    switch (data.type) {
      case 'announcement_section':
        parsed = this.parseAnnouncementSection(data);
        break;

      case 'stage_section':
        parsed = this.parseStageSection(data, context);
        break;

      case 'hero_section':
        parsed = this.parseHeroSection(data, context);
        break;

      case 'title_section':
        parsed = this.parseHeadlineSection();
        break;

      case 'square_section':
        parsed = this.parseSquareSection(data, context);
        break;

      case 'staggered_section': // TBD: might be redundant. check if is removable
      case 'equal_section':
        parsed = this.parseEqualSection(data, context);
        break;

      case 'info_section':
        parsed = InfoSectionAdapter.parse(data);
        break;

      case 'carousel_section':
        parsed = this.parseCarouselSection(data, context);
        break;

      case 'event_ribbon':
        parsed = this.parseEventRibbonSection();
        break;

      case 'test_cars_section':
        parsed = this.parseTestCarSection(data);
        break;

      case 'test_routes_section':
        parsed = this.parseTestRouteSection(data);
        break;

      case 'agenda_section':
        parsed = parseAgendaSection(data);
        break;

      case 'highlight_section':
        parsed = this.highlightAdapter.parse(data);
        break;

      case 'presskit_section':
        parsed = this.pressKitAdapter.parseSection(data);
        break;

      case 'contact_section':
        parsed = this.parseContactSection(data);
        break;

      case 'event_registration_section':
        parsed = this.parseEventRegistrationSection(data);
        break;

      case 'event_contacts_section':
        parsed = this.parseEventContactsSection(data);
        break;

      case 'event_experts_section':
        parsed = this.parseEventExpertsSection(data);
        break;

      case 'dynamic_content_section':
        parsed = this.parseDynamicContentSection(data);
        break;

      case 'dynamic_media_section':
        parsed = this.parseDynamicMediaSection(data);
        break;

      case 'dynamic_event_section':
        parsed = this.parseDynamicEventSection(data);
        break;
    }

    return Object.assign(
      {
        headline: data.title,
        anchor: data.anchor_link,
      },
      parsed
    );
  }

  private parseAnnouncementSection(data: any): AnnouncementBannerInterface {
    const announcementData = data.announcement_banner;
    const currentLang = this.translateService.currentLang.split('-')[0];
    const imageStyle =
      announcementData.banner_layout === AnnouncementBannerLayoutType.NORMAL
        ? AnnouncementMultiImageStyle
        : AnnouncementSmallImageStyle;

    return {
      id: announcementData.id,
      type: SectionType.ANNOUNCEMENT,
      title: announcementData.title,
      subtitle: announcementData.subtitle,
      fuelLabel: announcementData?.fuel_label_text?.processed,
      links: announcementData.buttons.map((button: any) => {
        return {
          label: button.title,
          link: button.slug
            ? (button.slug.find(({ lang }: any) => lang === currentLang) || button.slug[0]).value.replace('/page', '')
            : '/',
        };
      }),
      titleHash: announcementData.title_hash,
      layout: announcementData.banner_layout,
      background: this.mediaAdapter.parse(announcementData.background, imageStyle) as
        | MediaImageInterface
        | MediaVideoInterface,
      streams: announcementData.streams.map((stream: any) => {
        return {
          slug: stream.page_card.slug[0]?.value.replace('/page', '') || '/',
          stream: stream.stream ? this.streamAdapter.parse(stream.stream, StreamCardImageStyle) : null,
        };
      }),
      teasers: announcementData.announcements.map((announcement: any): AnnouncementInterface => {
        return {
          title: announcement.title,
          subtitle: announcement.subtitle,
          countdownDate: new Date(announcement.countdown_date),
          showDaysOnly: announcement.show_days_only,
          countdownLabel: announcement.countdown_label,
          postCountdownLabel: announcement.post_countdown_label,
        };
      }),
    };
  }

  private parseStageSection(data: any, context: FavoriteDataContextInterface) {
    const items = data.cards.map((card: any) => {
      let media = null;
      if (card.media) {
        media = this.mediaAdapter.parse(card.media, StageMediaCardImageStyle, context);
      } else if (card.stream) {
        media = this.streamAdapter.parse(card.stream, StreamCardImageStyle, context);
      }
      return {
        title: card.title,
        topline: card.topline,
        subtitle: card.subtitle,
        fuelLabel: card.fuel_label_text?.processed || '',
        media,
      };
    });
    return {
      type: SectionType.STAGE,
      items,
    };
  }

  private parseHeroSection(data: any, context: FavoriteDataContextInterface, isEqualSection = false) {
    switch (data.cards.length) {
      case 1:
      case 2:
        return {
          type: SectionType.HERO,
          isEqualSection,
          teasers: data.cards.map((item: any) => {
            return this.magazineCardAdapter.parse(
              item,
              data.cards.length === 1 ? Hero1SectionImageStyle : Hero2SectionImageStyle,
              context
            );
          }),
        };
      default:
        return {
          type: SectionType.HERO,
          teasers: data.cards.map((item: any, index: number) => {
            return this.magazineCardAdapter.parse(
              item,
              index ? Hero3SectionImageStyleSmall : Hero3SectionImageStyleLarge,
              context
            );
          }),
        };
    }
  }

  private parseHeadlineSection() {
    return {
      type: SectionType.HEADLINE,
    };
  }

  private parseSquareSection(data: any, context: FavoriteDataContextInterface) {
    const [largeTeaser, ...smallTeasers] = data.cards;
    return {
      type: SectionType.SQUARE,
      inverted: data.square_position === 'left',
      teasers: [
        this.magazineCardAdapter.parse(largeTeaser, SquareSectionImageStyleLarge, context),
        ...smallTeasers.map((item: any) => {
          return this.magazineCardAdapter.parse(item, SquareSectionImageStyleSmall, context);
        }),
      ],
    };
  }

  private parseEqualSection(data: any, context: FavoriteDataContextInterface) {
    switch (data.cards.length) {
      case 1:
      case 2:
        return this.parseHeroSection(data, context, true);
      case 3:
        return {
          type: SectionType.EQUAL_3,
          teasers: data.cards.map((item: any) => {
            return this.magazineCardAdapter.parse(item, Equal3SectionImageStyle, context);
          }),
        };
      case 4:
        return {
          type: SectionType.EQUAL_4,
          teasers: data.cards.map((item: any) => {
            return this.magazineCardAdapter.parse(item, Equal4SectionImageStyle, context);
          }),
        };
      case 5:
      case 6:
      case 7:
        const [largeTeaser, ...smallTeasers] = data.cards;
        return {
          type: SectionType.SQUARE,
          teasers: [
            this.magazineCardAdapter.parse(largeTeaser, SquareSectionImageStyleLarge, context),
            ...smallTeasers.map((item: any) => {
              return this.magazineCardAdapter.parse(item, StaggeredSectionImageStyleSmall, context);
            }),
          ],
        };
    }
    const [StaggeredLargeTeaser, ...StaggeredSmallTeasers] = data.cards;
    return {
      type: SectionType.STAGGERED,
      teasers: [
        this.magazineCardAdapter.parse(StaggeredLargeTeaser, StaggeredSectionImageStyleLarge, context),
        ...StaggeredSmallTeasers.map((item: any) => {
          return this.magazineCardAdapter.parse(item, StaggeredSectionImageStyleSmall, context);
        }),
      ],
    };
  }

  private parseCarouselSection(data: any, context: FavoriteDataContextInterface) {
    let imageStyle = CarouselSectionSquareImageStyle;
    switch (data.card_layout) {
      case CarouselLayoutTypes.NINE_SIXTY:
        imageStyle = CarouselSection_9_16_ImageStyle;
        break;
      case CarouselLayoutTypes.LANDSCAPE:
        imageStyle = CarouselSectionLandscapeImageStyle;
        break;
      case CarouselLayoutTypes.PORTRAIT:
        imageStyle = CarouselSectionPortraitImageStyle;
        break;
    }
    return {
      type: SectionType.CAROUSEL,
      layout: data.card_layout,
      teasers: data.cards.map((card: MagazineCardResponse) =>
        this.magazineCardAdapter.parse(card, imageStyle, context)
      ),
    };
  }

  private parseTestCarSection(data: any) {
    return {
      id: data.id,
      type: SectionType.TEST_CAR,
      teasers: data.test_cars
        .map((car: any) => {
          const parsed = TestCarSchema.safeParse(car);
          if (!parsed.success) {
            console.error('Error parsing magazine/test_car', parsed.error);
            return null;
          }
          parsed.data.image = parsed.data.image
            ? this.mediaAdapter.parseStructuredImage(parsed.data.image, TestCarImageStyle, null)
            : undefined;
          parsed.data.document = parsed.data.document ? this.mediaAdapter.parse(parsed.data.document) : undefined;
          return parsed.data;
        })
        .filter(Boolean),
    };
  }

  private parseTestRouteSection(data: any) {
    return {
      id: data.id,
      type: SectionType.TEST_ROUTE,
      latCenter: data.center_lat,
      lngCenter: data.center_lng,
      previewSmall: data.preview_map_small ? this.mediaAdapter.parseStaticImage(data.preview_map_small.uri.url) : '',
      previewLarge: data.preview_map_large ? this.mediaAdapter.parseStaticImage(data.preview_map_large.uri.url) : '',
      routes: data.test_routes.map((route: TestRouteResponse) => this.testRouteAdapter.parse(route)),
    };
  }

  private parseContactSection(data: any) {
    return {
      type: SectionType.CONTACT,
      contacts: data.contacts.map((contact: ContactPersonResponse) => this.contactAdapter.parse(contact)),
    };
  }

  private parseEventExpertsSection(data: any) {
    // currently mostly a placeholder
    return {
      type: SectionType.EVENT_EXPERTS,
      eventId: data.eventId,
      hideInitially: true,
    };
  }

  private parseEventRegistrationSection(data: any) {
    // currently mostly a placeholder
    return {
      type: SectionType.EVENT_REGISTRATION,
      eventId: data.eventId,
      hideInitially: true,
    };
  }

  private parseEventContactsSection(data: any) {
    // currently mostly a placeholder
    return {
      type: SectionType.EVENT_CONTACTS,
      eventId: data.eventId,
      hideInitially: true,
    };
  }

  private parseEventRibbonSection() {
    // currently mostly a placeholder
    return {
      type: SectionType.EVENT_RIBBON,
    };
  }

  private parseDynamicContentSection(data: any) {
    return {
      type: SectionType.DYNAMIC,
      topicId: data.topic_tree?.drupal_internal__tid,
      filterGroups: data.tag_filters.map((filter: any) => filter.drupal_internal__tid) || [],
      title: data.title,
      entities: ['PRESS_INFORMATION', 'PRESS_KIT'],
      assetsCount: {
        PRESS_INFORMATION: data.article_count,
        PRESS_KIT: data.presskit_count,
      },
      hasContent: data.article_count || data.presskit_count || !data.topic_tree?.drupal_internal__tid,
      showShowAllButton: true,
      customHeadline: true,
    };
  }

  private parseDynamicMediaSection(data: any) {
    return {
      type: SectionType.DYNAMIC,
      topicId: data.topic_tree?.drupal_internal__tid,
      filterGroups: data.tag_filters.map((filter: any) => filter.drupal_internal__tid) || [],
      title: data.title,
      entities: ['IMAGE', 'VIDEO', 'DOCUMENT', 'TECHNICAL_DATA'],
      assetsCount: {
        AUDIO: data.audio_count,
        DOCUMENT: data.document_count,
        IMAGE: data.image_count,
        VIDEO: data.video_count,
        TECHNICAL_DATA: data.technical_data,
      },
      hasContent:
        data.audio_count ||
        data.document_count ||
        data.image_count ||
        data.document_count ||
        data.technical_data ||
        !data.topic_tree?.drupal_internal__tid,
      showShowAllButton: true,
      customHeadline: true,
    };
  }

  private parseDynamicEventSection(data: any) {
    return {
      type: SectionType.DYNAMIC,
      topicId: data.topic_tree?.drupal_internal__tid,
      filterGroups: data.tag_filters?.map((filter: any) => filter.drupal_internal__tid) || [],
      title: data.title,
      entities: ['EVENT'],
      assetsCount: {
        EVENT: data.event_count || 1,
      },
      hasContent: data.event_count || true,
      showSingleTabNav: false,
      showShowAllButton: false,
      customHeadline: true,
    };
  }
}
